import useQuery from "utils/hooks/useQuery";
import { localStorageService } from "utils/localStorageService";
import { useEffect } from "react";
import {
  appendSubGroupId,
  getSignalExtraDetailsRoute,
  getSignalFollowData,
} from "utils/routes";
import { useHistory } from "react-router-dom";
import {
  NEXT_URL_QUERY_PARAM,
  SECURITY_SETTINGS_PATH,
  USER_SIGNALS_PATH,
} from "common/constants";
import { useIsOnboarding } from "utils/hooks/misc";
import { useSelector } from "react-redux";
import { UserSource } from "@shared/types";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import {
  LOCAL_STORAGE_REGISTRATION_DATA_KEY,
  SECONDS_IN_DAY,
} from "@shared/common/constants";
import { removeLocalStorageSignalRefKey } from "utils/misc";
import { SIGNAL_EXTRA_DETAILS_TABS } from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";

const getParsedLocalStorageRegistrationData = () => {
  const registrationData = localStorageService.getValue(
    LOCAL_STORAGE_REGISTRATION_DATA_KEY
  );
  return registrationData ? JSON.parse(registrationData) : undefined;
};

export const useSaveSearchParams = () => {
  const query = useQuery();
  const { actionAndSignalId, subGroupId, ref } = getSignalFollowData(query);
  const isOnboarding = useIsOnboarding();

  useEffect(() => {
    if (ref)
      localStorageService.setValue(
        LOCAL_STORAGE_REGISTRATION_DATA_KEY,
        JSON.stringify({
          actionAndSignalId,
          subGroupId,
          ref,
          timestamp: Date.now(),
          isOnboarding,
        })
      );
  }, []);

  useEffect(() => {
    const registrationData = getParsedLocalStorageRegistrationData();
    if (registrationData) {
      registrationData.isOnboarding = isOnboarding;
      localStorageService.setValue(
        LOCAL_STORAGE_REGISTRATION_DATA_KEY,
        JSON.stringify(registrationData)
      );
    }
  }, [isOnboarding]);
};

export const useConsumeSearchParams = () => {
  const history = useHistory();
  const isOnboarding = useIsOnboarding();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const { getUpdatedPath } = useHistoryWithQuery();
  const parsedRegistrationData = getParsedLocalStorageRegistrationData();
  const isSignalRefValid =
    (Date.now() - parsedRegistrationData?.timestamp) / 1000 <
    SECONDS_IN_DAY * 3;

  const consumeSignalSearchParam = () => {
    let shouldClearLocalStorage = true;
    if (isSignalRefValid) {
      const { actionAndSignalId, subGroupId, ref, isOnboarding } =
        parsedRegistrationData;
      const isExternalAccountConnected =
        (ref === UserSource.DISCORD && user?.is_discord_connected) ||
        (ref === UserSource.TELEGRAM && user?.is_telegram_connected);

      if (!isOnboarding && ((actionAndSignalId && subGroupId) || ref)) {
        if (isExternalAccountConnected) {
          const regexMatch = actionAndSignalId?.match(
            new RegExp("(\\w*)-(\\d+)")
          );
          if (subGroupId && regexMatch?.[2]) {
            history.push(
              appendSubGroupId(
                getSignalExtraDetailsRoute(
                  USER_SIGNALS_PATH,
                  parseInt(regexMatch[2]),
                  SIGNAL_EXTRA_DETAILS_TABS.GENERAL,
                  false
                ),
                parseInt(subGroupId)
              )
            );
          }
        } else {
          history.push(
            getUpdatedPath(
              undefined,
              [NEXT_URL_QUERY_PARAM],
              SECURITY_SETTINGS_PATH
            )
          );
          shouldClearLocalStorage = false;
        }
      }
    }
    if (shouldClearLocalStorage && parsedRegistrationData)
      removeLocalStorageSignalRefKey();
  };

  useEffect(() => {
    if (!isOnboarding) {
      consumeSignalSearchParam();
    }
  }, [isOnboarding]);
};
